import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

const ROW_KEYS = [
    { label: "comp.row1.label", classic: "comp.row1.classic", itly: "comp.row1.itly" },
    { label: "comp.row2.label", classic: "comp.row2.classic", itly: "comp.row2.itly" },
    { label: "comp.row3.label", classic: "comp.row3.classic", itly: "comp.row3.itly" },
    { label: "comp.row4.label", classic: "comp.row4.classic", itly: "comp.row4.itly" },
    { label: "comp.row5.label", classic: "comp.row5.classic", itly: "comp.row5.itly" },
    { label: "comp.row6.label", classic: "comp.row6.classic", itly: "comp.row6.itly" },
    { label: "comp.row7.label", classic: "comp.row7.classic", itly: "comp.row7.itly" },
];

/**
 * Comparison section — desktop shows a 3-column grid table.
 * Below 720px we render a stacked list of rows (label + two blocks) for readability.
 */
export default function ComparisonSection() {
    const { t } = useI18n();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 720px)");
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    const rows = ROW_KEYS.map((r) => ({
        label: t(r.label),
        classic: t(r.classic),
        itly: t(r.itly),
    }));

    return (
        <section
            id="confronto"
            data-testid="comparison"
            style={{
                background: "var(--itly-surface)",
                borderTop: "1px solid var(--itly-border)",
                borderBottom: "1px solid var(--itly-border)",
                padding: "80px 24px",
            }}
            data-reveal="up"
        >
            <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                <div style={{ maxWidth: 760, marginBottom: 40 }}>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 12.5,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-700)",
                            margin: 0,
                        }}
                    >
                        {t("comp.badge")}
                    </p>
                    <h2
                        className="itly-serif"
                        style={{
                            marginTop: 14,
                            marginBottom: 0,
                            fontSize: "clamp(28px, 3.6vw, 48px)",
                            lineHeight: 1.08,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.022em",
                        }}
                    >
                        {t("comp.title_a")}{" "}
                        <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>
                            {t("comp.title_b")}
                        </em>
                    </h2>
                </div>

                {isMobile ? (
                    <MobileList rows={rows} t={t} />
                ) : (
                    <DesktopTable rows={rows} t={t} />
                )}

                <p
                    style={{
                        marginTop: 20,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12.5,
                        color: "var(--itly-muted)",
                        lineHeight: 1.5,
                        maxWidth: 720,
                    }}
                >
                    {t("comp.disclaimer")}
                </p>
            </div>
        </section>
    );
}

function DesktopTable({ rows, t }) {
    return (
        <div
            className="itly-comparison-table"
            data-testid="comparison-table"
            role="table"
            style={{
                display: "grid",
                gridTemplateColumns: "minmax(160px, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)",
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: "var(--itly-card-shadow)",
            }}
        >
            {/* Header row */}
            <div style={headStyle} />
            <div style={{ ...headStyle, background: "var(--itly-cream)" }}>
                <span style={headLabel}>{t("comp.headClassic")}</span>
            </div>
            <div style={{ ...headStyle, background: "var(--itly-teal-900)" }}>
                <span style={{ ...headLabel, color: "var(--itly-orange-500)" }}>{t("comp.headItly")}</span>
            </div>

            {rows.map((r) => (
                <RowGroup key={r.label} r={r} />
            ))}
        </div>
    );
}

function RowGroup({ r }) {
    const cellBase = {
        padding: "18px 22px",
        borderTop: "1px solid var(--itly-border)",
        fontFamily: "Inter, sans-serif",
        fontSize: 14.5,
        lineHeight: 1.5,
    };
    return (
        <>
            <div style={{ ...cellBase, fontWeight: 600, color: "var(--itly-fg-strong)", background: "var(--itly-surface-2)" }}>
                {r.label}
            </div>
            <div style={{ ...cellBase, color: "var(--itly-muted)", background: "var(--itly-surface)" }}>
                <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 8 }}>
                    <XIcon />
                    {r.classic}
                </span>
            </div>
            <div style={{ ...cellBase, color: "var(--itly-fg-strong)", background: "var(--itly-surface-teal)", fontWeight: 500 }}>
                <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 8 }}>
                    <CheckIcon />
                    {r.itly}
                </span>
            </div>
        </>
    );
}

/* Mobile: stacked cards, each row is a compact card with label + 2 blocks */
function MobileCard({ r, t }) {
    return (
        <div
            style={{
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderRadius: 14,
                overflow: "hidden",
                boxShadow: "var(--itly-card-shadow)",
            }}
        >
            <div
                style={{
                    padding: "12px 16px",
                    background: "var(--itly-surface-2)",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: "var(--itly-fg-strong)",
                    borderBottom: "1px solid var(--itly-border)",
                }}
            >
                {r.label}
            </div>
            <div
                style={{
                    padding: "14px 16px",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: "var(--itly-muted)",
                }}
            >
                <XIcon />
                <div style={{ flex: 1 }}>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "#8B8474",
                            marginBottom: 2,
                        }}
                    >
                        {t("comp.headClassic")}
                    </div>
                    {r.classic}
                </div>
            </div>
            <div
                style={{
                    padding: "14px 16px",
                    background: "var(--itly-surface-teal)",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    fontFamily: "Inter, sans-serif",
                    fontSize: 14.5,
                    lineHeight: 1.5,
                    color: "var(--itly-fg-strong)",
                    fontWeight: 500,
                    borderTop: "1px solid var(--itly-border)",
                }}
            >
                <CheckIcon />
                <div style={{ flex: 1 }}>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-700)",
                            marginBottom: 2,
                        }}
                    >
                        {t("comp.headItly")}
                    </div>
                    {r.itly}
                </div>
            </div>
        </div>
    );
}

function MobileList({ rows, t }) {
    return (
        <div
            data-testid="comparison-mobile-list"
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
            {rows.map((r) => <MobileCard key={r.label} r={r} t={t} />)}
        </div>
    );
}

const headStyle = {
    padding: "18px 22px",
    background: "var(--itly-surface-2)",
};
const headLabel = {
    fontFamily: "Inter, sans-serif",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--itly-fg-strong)",
};

function CheckIcon() {
    return (
        <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: "#25A26A", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 1 }} aria-hidden="true">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
        </span>
    );
}
function XIcon() {
    return (
        <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: "var(--itly-map-region)", color: "#8B8474", display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 1 }} aria-hidden="true">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M6 18L18 6" /></svg>
        </span>
    );
}
